import styled from '@emotion/styled'
import React from 'react'
import { useBillingPortal } from './useBillingPortal'
import { Hugeicon } from '../../../icons/Hugeicon'

type Props = {
  organizationId: string
}

/**
 * Prominent recovery banner shown at the top of the dashboard when the
 * subscription is past_due. The calendar stays live during the grace period,
 * so the messaging is urgent but reassuring, and the CTA drops the owner
 * straight into the Stripe billing portal to update their card.
 */
const PastDueBanner: React.FC<Props> = ({ organizationId }) => {
  const { openPortal, loading } = useBillingPortal(organizationId)

  return (
    <Banner role="alert">
      <IconWrapper>
        <AlertIcon />
      </IconWrapper>
      <Content>
        <Headline>Your payment didn&apos;t go through</Headline>
        <Body>Update your card to keep your calendar online — it&apos;s still live for now.</Body>
      </Content>
      <UpdateButton onClick={() => void openPortal()} disabled={loading}>
        {loading ? 'Loading…' : 'Update payment method'}
      </UpdateButton>
    </Banner>
  )
}

export default PastDueBanner

const AlertIcon = () => (
  <Hugeicon name="alert" size={22} />
)

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-left: 4px solid #dc2626;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 24px;

  @media (max-width: 640px) {
    flex-wrap: wrap;
  }
`

const IconWrapper = styled.div`
  flex-shrink: 0;
  color: #dc2626;
  display: flex;
  align-items: center;
`

const Content = styled.div`
  flex: 1;
  min-width: 0;
`

const Headline = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #991b1b;
  margin-bottom: 2px;
`

const Body = styled.div`
  font-size: 13px;
  color: #b91c1c;
  line-height: 1.5;
`

const UpdateButton = styled.button`
  flex-shrink: 0;
  background: #dc2626;
  color: white;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: #b91c1c;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`
